import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { PersDataApiFrom } from '@api-gen/models/pers-data-api-from';
import { PersdataItemApiFrom } from '@api-gen/models/persdata-item-api-from';
import { LanguageState } from '@core/localization/language.state';
import { NetworkStatus } from '@core/models/network-status';
import { AppState } from '@core/state/app.state';
import { UserState } from '@core/state/user.state';
import { SessionStorage } from '@core/storage/session-storage';
import { StorageKeys } from '@core/storage/storage.keys';
import { RxEffects } from '@rx-angular/state/effects';
import {
    ExpiresPrivilegeDialogComponent,
} from '@shared/components/layout/expires-privilege-dialog/expires-privilege-dialog.component';
import {
    NetworkStatusInfoComponent,
} from '@shared/components/layout/network-status-info/network-status-info.component';
import { MatDialogService } from '@shared/services';
import { Observable } from 'rxjs';
import { delay, filter } from 'rxjs/operators';


@Component({
    selector: 'app-root',
    imports: [
        RouterOutlet,
        AsyncPipe,
        NetworkStatusInfoComponent,
    ],
    templateUrl: './root.html',
    styleUrl: './root.scss',
    providers: [RxEffects],
})
export class Root {
    @SessionStorage(StorageKeys.SkipExpiresPrivilegeFlag)
    public skipExpiresPrivilegeFlag: boolean;

    readonly #effects: RxEffects = inject(RxEffects);
    readonly #userState: UserState = inject(UserState);
    readonly #appState: AppState = inject(AppState);
    readonly #languageState: LanguageState = inject(LanguageState);
    readonly #dialog: MatDialogService = inject(MatDialogService);

    public readonly networkStatus$: Observable<NetworkStatus> = this.#appState.networkStatus$;
    public readonly isAuthorized$: Observable<boolean> = this.#userState.isAuthorized$;

    constructor() {
        this.#languageState.requestLanguages();
        this.loadUnAuthorizedUserData();

        this.#effects.register(this.#userState.isAuthorized$.pipe(delay(0)),
            (isAuthorized: boolean) => {
                if (isAuthorized) {
                    this.#userState.requestBalance();
                    this.#userState.requestAutoPaymentFlags();
                    this.loadAuthorizedUserData();
                }
            });

        this.#effects.register(
            this.#userState.profile$.pipe(filter(Boolean)),
            (profile: PersDataApiFrom) => {
                if (profile.privs?.find((privilege: PersdataItemApiFrom) => !!privilege.expSoon) && !this.skipExpiresPrivilegeFlag) {
                    this.#dialog.open(ExpiresPrivilegeDialogComponent, {
                        data: { privileges: profile.privs.filter((privilege: PersdataItemApiFrom) => !!privilege.expSoon) },
                    });
                }
            },
        );
    }

    private loadUnAuthorizedUserData(): void {
        this.#appState.requestRoamingRegions();
        this.#appState.requestCountryCodes();
        this.#appState.requestRoamingSettings();
        this.#appState.requestCommunicationChannels();
        this.#appState.requestCommunicationChannelsInfo();
    }

    private loadAuthorizedUserData(): void {
        this.#appState.requestRegions();
        this.#userState.requestTickets();
        this.#appState.requestSettings();
        this.#userState.requestProfile();
        this.#userState.requestNotificationSettings();
        this.#userState.requestBonusFunds();
        this.#userState.requestMediums();
    }
}
